/**
 * Timeline - Figma `6377:88174`.
 *
 * A black band, inside the content grid (it ran to the viewport edges until 2026-10-30; what
 * runs past the grid's edge is clipped and scrolled to): previous/next buttons top left, a row of years top right, and
 * below them columns that scroll sideways. Each column shows its year set on its side
 * against a rule, with that year's entries stacked at the foot of the column.
 *
 * **The content model is flat and the design is grouped.** `shared.timeline-item` is one
 * year plus one month, so several items share a year; the renderer groups them. That is
 * the right way round - an editor adds one event at a time and does not have to think
 * about columns.
 *
 * The year row is navigation, not decoration: each year scrolls its column into view.
 * The columns are a real scroll container, so a trackpad, a touch screen and a keyboard
 * all work without the buttons.
 *
 * The band paints its own black rather than relying on the section's `background`
 * setting - it is intrinsic to this design, not an editor's choice. The file's band is
 * `#000000` (sampled from 6377:88174); it was `ink-strong`, a dark grey, until 2026-10-30.
 * `fullBleed` is ignored for the same reason as in `pillars`: pages saved with it ticked
 * would otherwise drop out of the grid.
 */
import { SectionShell } from '../primitives';
import { ScrollerButtons, TRACK, useScroller } from '../shared/Scroller';
import type { TimelineItem, TimelineSection } from '../../types/sections';

/** Items grouped by year, in the order the years first appear. */
function groupByYear(items: TimelineItem[]): { year: string; entries: TimelineItem[] }[] {
  const order: string[] = [];
  const byYear = new Map<string, TimelineItem[]>();

  for (const item of items) {
    const year = item.year ?? '';
    if (!byYear.has(year)) {
      byYear.set(year, []);
      order.push(year);
    }
    byYear.get(year)!.push(item);
  }

  return order.map((year) => ({ year, entries: byYear.get(year)! }));
}

export default function Timeline({ data }: { data: TimelineSection }) {
  const { track, scrollBy, scrollTo, range } = useScroller();
  const years = groupByYear(data.items ?? []);
  const label = data.heading ?? 'Historia';

  if (!years.length) return null;

  return (
    <SectionShell settings={{ ...data.settings, fullBleed: false }} name="timeline">
      {data.heading ? <h2 className="text-h4 text-ink">{data.heading}</h2> : null}

      <div className="overflow-hidden rounded-l bg-black px-5 py-10 text-white sm:px-8 lg:px-10">
        {/* On a phone the years are one row that scrolls on its own, below the buttons: with a
            dozen of them a wrapping list was five lines of dates above the content. From `md`
            up the design's layout is back - buttons left, years right, wrapping if need be. */}
        <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-6">
          <ScrollerButtons label={label} scrollBy={scrollBy} range={range} tone="dark" />

          <nav
            aria-label={`${label}: lata`}
            className="-mx-5 flex gap-5 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:-mx-8 sm:px-8 md:mx-0 md:flex-wrap md:gap-4 md:overflow-visible md:px-0"
          >
            {years.map((group, i) => (
              <button
                key={group.year}
                type="button"
                onClick={() => scrollTo(i)}
                className="shrink-0 py-2 text-body-sm text-white/60 underline-offset-8 transition hover:text-white hover:underline md:py-0"
              >
                {group.year}
              </button>
            ))}
          </nav>
        </div>

        <div
          ref={track}
          role="group"
          aria-label={label}
          tabIndex={0}
          className={`${TRACK} mt-8 md:mt-10`}
        >
          {/* A phone's column is as wide as the row less a sliver of the next one, which says
              "this scrolls" and leaves no dead black beside the text; its year is set flat above
              the entries, because the vertical one is a column of its own and left the right
              half of every card empty. The design's vertical year returns from `md`. */}
          {/* `relative` on the column is not styling. The `sr-only` year heading is absolutely
              positioned, and with no positioned ancestor its containing block is the page, so
              neither the track nor the band clips it: every column added one more invisible
              element further right and the whole page became that much wider to scroll
              sideways - blank, because nothing is there. Measured: 16 years, a 792 px viewport,
              a 4829 px document; 792 with the column positioned. */}
          {years.map((group) => (
            <section
              key={group.year}
              className="relative flex w-[85%] shrink-0 snap-start flex-col gap-6 border-l border-white/30 pl-5 md:min-h-[320px] md:w-[300px] md:justify-between md:gap-10 md:pl-6"
            >
              <p aria-hidden className="text-h3 leading-none md:text-h1 md:[writing-mode:vertical-rl] md:rotate-180">
                {group.year}
              </p>
              <h3 className="sr-only">{group.year}</h3>

              <div className="flex flex-col gap-6">
                {group.entries.map((entry, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    {entry.month ? <p className="text-body-sm font-semibold">{entry.month}</p> : null}
                    {entry.title ? <p className="text-body-sm">{entry.title}</p> : null}
                    {entry.body ? <p className="text-body-sm text-white/70">{entry.body}</p> : null}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
